<template>
    <div class="container pb-md">
        <h4 class="mb-md w-100 d-flex align-center gap-sm">
            <span>My orders</span>
            <span class="chips chips--circle" v-if="total > 0">{{ total }}</span>
        </h4>
        <div class="orders__items" ref="itemsContainer" v-if="total > 0">
            <div v-if="isLoading" class="loader-wrapper">
                <div class="spinner"></div>
            </div>
            <template v-if="items.length">
                <div class="bg-light-gray radius-md py-md px-md mb-sm" v-for="item in items" :key="item.hash">
                    <div class="d-flex align-center justify-between orders__item">
                        <div class="text-small text-dark-gray">
                            <div class="mb-xs">
                                Order from <strong>{{ formatTime( item.created_at ) }}</strong>
                            </div>
                            <div>
                                <strong class="text-black">{{ item.hash }}</strong>
                                {{ item.items.length }} Item{{ item.items.length > 1 ? 's' : '' }}
                            </div>
                        </div>
                        <div class="grow"></div>
                        <div class="d-flex align-center gap-sm orders__item-price">
                            <template v-if="item.amount_internal > 0">
                                <div class="d-flex text-small bg-light-green fw-bold py-xs px-sm radius-md">
                                    <i class="icon-gift"></i> <span>Grant applied</span>
                                </div>
                                <div class="d-flex align-center gap-sm">
                                    <div class="fw-bold">
                                        {{ NumberFormat.format( item.amount / 100 ) }}
                                    </div>
                                    <div class="text-strike text-small text-dark-gray">
                                        {{ NumberFormat.format( item.amount_internal / 100 ) }}
                                    </div>
                                </div>
                            </template>
                            <div class="fw-bold" v-else>
                                {{ NumberFormat.format( item.amount / 100 ) }}
                            </div>
                        </div>
                        <a
                            @click="item.expanded = !item.expanded"
                            class="cursor-pointer"
                            :style="[
                                    item.expanded ? 'transform: rotate(180deg)' : '',
                                    'transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);'
                                ]"
                        >
                            <i class="icon-arrow_down"></i>
                        </a>
                    </div>
                    <transition name="slide-fade">
                        <div class="expand-items" v-if="item.expanded">
                            <div class="expand-item"
                                 v-for="it in item.items" :key="`${it.product_id}___${it.variation_id}`">
                                <div class="expand-item__thumbnail">
                                    <img :src="it.thumbnail" loading="lazy" />
                                </div>
                                <div class="expand-item__title">
                                    <div class="pb-sm">{{ it.name }}</div>
                                    <div class="item-price fw-bold">
                                        {{ NumberFormat.format( (it.price * it.quantity) / 100 ) }}
                                    </div>
                                </div>
                                <template v-if="!!it?.digital && !!it.digital.file">
                                    <div class="grow"></div>
                                    <a class="btn btn--blue" target="_blank" :href="it.digital.file">Download</a>
                                </template>
                            </div>
                        </div>
                    </transition>
                </div>
            </template>
            <Pagination
                :total="total"
                :page-size="options.page_size"
                v-model:current-page="options.page"
                @update:current-page="loadData"
            />
        </div>

        <div class="bg-light-gray radius-md py-md px-md" v-else-if="items.length === 0 && ! isLoading">
            <div class="d-flex align-center justify-between gap-md">
                <div>
                    <div class="text-extra fw-bold mb-sm">No orders yet.</div>
                    <div>Your purchased materials will appear here</div>
                </div>
                <a :href="`https://${domain}`" class="btn btn--blue">Start shopping</a>
            </div>
        </div>
    </div>
</template>

<script>

import {defineComponent, toRaw} from 'vue'
import Pagination from "@/BinaryFlow/components/Common/Pagination.vue";

export default defineComponent({
    name: "orders",
    components: {Pagination},
    data() {
        return {
            isLoading: false,
            user: {
                email: '',
                first_name: '',
                last_name: '',
                country: '',
                phone: '',
            },
            total: 0,
            items: [],
            stats: window.stats || [],
            NumberFormat: {},
            options: {
                page: 1,
                page_size: 5,
                order: 'desc',
                order_by: 'id'
            },
        }
    },
    created() {
        this.NumberFormat = new Intl.NumberFormat('en-US', {style: 'currency', currency: this.stats.currency || 'USD'});
    },
    mounted() {
        this.loadData();
    },
    computed: {
        domain() {
            return window.location.hostname.replace('www', '')
        }
    },
    methods: {
        /**
         * Инициализация страницы.
         * Запрос данных заказов.
         */
        async loadData() {

            if( this.isLoading )
                return;

            this.isLoading = true

            let obj = structuredClone( toRaw( this.options ) )

            try {
                const response = await this.$http.post('/account/listOrders', obj);

                const { status, code, data } = response.data;
                if( data?.transactions && Array.isArray( data.transactions ) ) {
                    this.items = data.transactions;
                    this.total = data.total;
                } else {
                    console.warn('Items not found in response, resetting to empty array');
                    this.items = [];
                    this.total = 0
                }
            } catch ( err ) {
                console.error('Error loading orders:', err);
            } finally {
                this.isLoading = false;
                if( window.innerWidth <= 768 ) {
                    await this.$nextTick(() => {
                        const el = this.$refs.itemsContainer;
                        const header = document.querySelector('header');
                        if (el) {
                            const rect = el.getBoundingClientRect();
                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                            const elementTop = rect.top + scrollTop;
                            const headerHeight = header ? header.offsetHeight : 80;
                            const offset = elementTop - headerHeight - 50;

                            window.scrollTo({
                                top: offset,
                                behavior: 'smooth'
                            });
                        }
                    });
                }
            }
        },
        formatTime( dateString, options = {} ) {

            if( ! dateString )
                return '';

            // Настройки по умолчанию
            const defaults = {
                locale: 'en-US',
                hour12: true,
                showSeconds: false,
                showYear: true,
                monthFormat: 'short' // '2-digit' для 10, 'short' для Oct, 'long' для October
            };

            const config = { ...defaults, ...options };

            const date = new Date(dateString.replace(/-/g, "/")); // Фикс для сафари

            return new Intl.DateTimeFormat(config.locale, {
                year: config.showYear ? 'numeric' : undefined,
                month: config.monthFormat,
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: config.showSeconds ? '2-digit' : undefined, // Динамическое вкл/выкл
                hour12: config.hour12
            }).format(date);
        }
    }
})
</script>

<style scoped>

</style>